import React from "react";
import { Box, Container, Typography, Chip } from "@mui/material";
import {
  Home,
  NavigateNext,
  Event as EventIcon,
  Store,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface EventsHeroProps {
  selectedMall?: {
    id: string;
    name: string;
  } | null;
  totalEvents: number;
}

/**
 * EventsHero Component
 *
 * Hero section for the Events page with breadcrumb navigation,
 * title, subtitle, and statistics chips
 */
const EventsHero: React.FC<EventsHeroProps> = ({
  selectedMall,
  totalEvents,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1a237e 100%)", // Premium navy
        color: "white",
        pt: { xs: 3, md: 4 },
        pb: { xs: 4, md: 5 },
        position: "relative",
        overflow: "hidden",
        zIndex: 1000,
        boxShadow: "0 4px 20px rgba(26, 35, 126, 0.3)",
      }}
    >
      {/* Gold accent pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage:
            "radial-gradient(circle, rgba(212, 175, 55, 0.4) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Animated background pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.12,
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.3) 0%, transparent 50%)`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Breadcrumb Navigation */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <Home
              sx={{
                fontSize: 20,
                cursor: "pointer",
                "&:hover": { opacity: 0.8 },
              }}
              onClick={() => navigate("/")}
            />
            <NavigateNext sx={{ fontSize: 20 }} />
            {selectedMall && (
              <>
                <Typography
                  variant="body2"
                  onClick={() => navigate("/explore")}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Explore
                </Typography>
                <NavigateNext sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  onClick={() => navigate(`/mall/${selectedMall.id}`)}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {selectedMall.name}
                </Typography>
                <NavigateNext sx={{ fontSize: 20 }} />
              </>
            )}
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Events
            </Typography>
          </Box>

          {/* Title and Subtitle */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Discover Exciting Events
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                fontWeight: 400,
                fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Find the latest events and activities at your favorite malls
            </Typography>
          </Box>

          {/* Stats Chips in Hero */}
          {selectedMall && (
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2, md: 3 },
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={<EventIcon />}
                label={`${totalEvents} Total Events`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 1.5 },
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
              <Chip
                icon={<Store />}
                label={selectedMall.name}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 1.5 },
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default EventsHero;
